import React from 'react';
import PropTypes from 'prop-types';

import {Modal} from '@albert-io/atomic';

import {invalidatePartialInterest} from 'resources/mandark.resource';
import {getModelForResourceType} from 'resources/modelRegistry';

import {GoogleClassroomImporter} from 'client/components/Google';
import ClassCodeModal from 'client/Classrooms/Classroom/Teacher/ManageRoster/ClassCodeModal/ClassCodeModal.react';

import {callAction} from 'client/framework';
import classroomV2Actions from 'client/Classrooms/Classroom/ClassroomV2.actions';

import ManageRosterContext from './ManageRoster.context';
import InviteByEmail from './InviteByEmail/InviteByEmail';
import BulkResendInvitations from './BulkResendInvitations/BulkResendInvitations';
import InviteByLMS from './InviteByLMS/InviteByLMS.react';

function confirmCloseModal() {
  return global.confirm("Are you sure you want to exit? You'll lose any unsaved changes.");
}

const InviteByEmailModal = ({handleClose, ...rest}) => (
  /**
   * handleClose bears our function for clearing the modal
   *
   * @param root0
   * @param root0.classroom
   * @param root0.queries
   * @param root0.setIsReady
   * @param root0.students
   */
  <ManageRosterContext.Consumer>
    {({classroom, queries, setIsReady, students}) => (
      <Modal
        ariaLabel='Invite By Email'
        handleClose={handleClose}
        closeModalPredicate={confirmCloseModal}
      >
        {({modalContentStyle}) => (
          <InviteByEmail
            className={modalContentStyle}
            classroomId={classroom.getId()}
            hardCap={classroom.isLicensed() && classroom.getSchool().isHardCap()}
            seatsRemaining={classroom.getMeta().getCountOfSchoolLicenseSeatsRemaining()}
            emailDomains={classroom.getSchool().getEmailDomains()}
            handleClose={() => {
              handleClose();
              setIsReady(false);
            }}
            hideCloseBtn={false}
            onSend={() => {
              // There's no guarantees about the table's current page, search, or sort state.
              // To invalidate all of these possible queries at once, we nuke the partial interest.
              invalidatePartialInterest(queries.invitations.done());
              callAction(classroomV2Actions.INVALIDATE_CLASSROOM);
            }}
            restrictClassroomEnrollment={classroom.getSchool().isRestrictClassroomEnrollment()}
            students={students}
            inModal
            size='l'
            {...rest}
          />
        )}
      </Modal>
    )}
  </ManageRosterContext.Consumer>
);

InviteByEmailModal.propTypes = {
  classroom: PropTypes.instanceOf(getModelForResourceType('classroom_v1')).isRequired,
  handleClose: PropTypes.func
};

const GoogleClassroomImporterModal = ({handleClose, ...rest}) => (
  <ManageRosterContext.Consumer>
    {({classroom, setIsReady}) => (
      <Modal
        ariaLabel='Import Email Addresses from Google Classroom'
        handleClose={handleClose}
        closeModalPredicate={confirmCloseModal}
      >
        {({modalContentStyle}) => (
          <GoogleClassroomImporter
            className={modalContentStyle}
            classroom={classroom}
            emailDomains={classroom.getSchool().getEmailDomains()}
            handleClose={handleClose}
            hideCloseBtn={false}
            inModal
            onImport={() => setIsReady(false)}
            onCancel={handleClose}
            restrictClassroomEnrollment={classroom.getSchool().isRestrictClassroomEnrollment()}
            title='Import Email Addresses from Google Classroom'
            {...rest}
          />
        )}
      </Modal>
    )}
  </ManageRosterContext.Consumer>
);

GoogleClassroomImporterModal.propTypes = {
  classroom: PropTypes.instanceOf(getModelForResourceType('classroom_v1')).isRequired,
  handleClose: PropTypes.func
};

const BulkResendInvitationsModal = ({handleClose}) => (
  <ManageRosterContext.Consumer>
    {({classroom, queries, setIsReady}) => (
      <Modal ariaLabel='Re-send Invitations' handleClose={handleClose}>
        {({modalContentStyle}) => (
          <BulkResendInvitations
            className={modalContentStyle}
            seatsRemaining={classroom.getMeta().getCountOfSchoolLicenseSeatsRemaining()}
            onConfirm={() => {
              invalidatePartialInterest(queries.invitations.done());
              setIsReady(false);
              handleClose();
            }}
            inModal
            onCancel={handleClose}
          />
        )}
      </Modal>
    )}
  </ManageRosterContext.Consumer>
);

BulkResendInvitationsModal.propTypes = {
  handleClose: PropTypes.func
};

const InviteByLmsModal = ({handleClose, ...rest}) => (
  /**
   * handleClose bears our function for clearing the modal
   *
   * @param root0
   * @param root0.setIsReady
   * @param root0.classroom
   */
  <ManageRosterContext.Consumer>
    {({setIsReady, classroom}) => (
      <Modal ariaLabel='Invite By LMS' handleClose={handleClose}>
        {({modalContentStyle}) => (
          <InviteByLMS
            className={modalContentStyle}
            classroom={classroom}
            handleClose={() => {
              handleClose();
              setIsReady(false);
            }}
            {...rest}
          />
        )}
      </Modal>
    )}
  </ManageRosterContext.Consumer>
);

InviteByLmsModal.propTypes = {
  handleClose: PropTypes.func
};

export default {
  email: InviteByEmailModal,
  bulkResend: BulkResendInvitationsModal,
  classCode: ClassCodeModal,
  googleClassroom: GoogleClassroomImporterModal,
  LMS: InviteByLmsModal
};
